/**
 * src/components/ProtectedRoute.tsx — the auth gate wrapped around `<ConverterApp />`.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/ProtectedRoute.tsx` (whole file, 25 lines).
 * Spec: `docs/spec/06-frontend-components.md` §1.4 (gating semantics), §1.3 (route table).
 *
 * Used once, by `src/pages/Index.tsx:6-8`:
 *     <ProtectedRoute><ConverterApp /></ProtectedRoute>
 *
 * Three branches, in this order (`ProtectedRoute.tsx:12-24`):
 *  1. `isLoading` → full-screen spinner. `AuthContext` starts `isLoading = true`, so this is what
 *     paints while `GET /api/auth/me` is in flight.
 *  2. `!isAuthenticated` → renders `<HexclaveAuthForm />` **in place**. It does NOT redirect and does
 *     NOT use `<Navigate>` — the URL stays `/` (spec §1.4.2). `/admin` gates itself differently
 *     (`Admin.tsx:18-20` redirects to `/`), so the sign-in card never appears at `/admin`.
 *     Note `isAuthenticated` is `!!user` where `user` is the **D1** row, so a visitor with a valid
 *     Hexclave session but no approved row lands here too. That is intentional, and it is why
 *     `HexclaveAuthForm` has more than one state.
 *  3. otherwise → `children`.
 *
 * ═══ WHAT THIS COMPONENT DELIBERATELY IS NOT ═════════════════════════════════════════════════
 * It is **not** an error boundary. DF-22 rank 6 (spec §15 D-19) requires that a render throw
 * anywhere below — e.g. `format(new Date(user.created_at), 'PPP p')` in `Admin.tsx:63` — whites out
 * the whole app, because the source has no `componentDidCatch` / `ErrorBoundary` anywhere in `src/`.
 * That is the long-standing "blank page" report and the owner is validating it visually first.
 * **Do not add an error boundary here or anywhere else.**
 *
 * It also does not handle 401/403 (DF-22 rank 5): an expired token leaves `isAuthenticated` true
 * from the restored session until a reload, and the failing queries render as "no documents".
 *
 * The spinner markup is verbatim from `:15` — a bare `<div>` with `animate-spin`, no
 * `role="status"`, no accessible label, no timeout, no retry.
 */
import type { ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { HexclaveAuthForm } from '@/components/HexclaveAuthForm'

interface ProtectedRouteProps {
  children: ReactNode
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth()

  // `ProtectedRoute.tsx:12-18`
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  // `ProtectedRoute.tsx:20-22` — rendered in place; the URL is not changed.
  if (!isAuthenticated) {
    return <HexclaveAuthForm />
  }

  // `ProtectedRoute.tsx:24`
  return <>{children}</>
}
